import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { AiPlanPreviewCard } from './AiPlanPreviewCard';
import { buildFollowUpSuggestions, FollowUpSuggestion } from './aiFollowUpSuggestions';
import {
  aiAPI,
  AiGraphFocusResponse,
  AiGraphRelationshipPathResponse,
  AiGraphResolvePersonResponse,
  AiGraphSearchPeopleResponse,
  AiGraphTraversalResponse,
  AiGraphTreeStatsResponse,
  AiReadResult,
  AiPendingActionPlan,
  AiPlanCommitResponse,
  AiPlannerResponse,
  AiSessionContext,
  peopleAPI,
  PersonDto,
} from '../services/api';
import { brandClasses } from '../theme';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

type AiCommandPanelProps = {
  treeId: string;
  treeName?: string;
  currentUserId?: string;
  onClose: () => void;
  onOpenPerson: (person: PersonDto) => void;
  onFocusPerson: (personId: string) => void;
  onSearchResults: (query: string, people: PersonDto[]) => void;
};

type CommandKind = 'resolve' | 'search' | 'ancestors' | 'descendants' | 'path' | 'focus' | 'stats' | 'help';

type ParsedCommand =
  | { kind: 'help' }
  | { kind: 'stats' }
  | { kind: 'resolve' | 'search' | 'focus'; query: string }
  | { kind: 'ancestors' | 'descendants'; query: string; depth: number }
  | { kind: 'path'; fromQuery: string; toQuery: string }
  | null;

type AiResult =
  | { kind: 'planner'; planner: AiPlannerResponse; note: string }
  | { kind: 'read'; response: AiReadResult; people: PersonDto[]; note: string }
  | { kind: 'commit'; response: AiPlanCommitResponse; note: string }
  | { kind: 'resolve'; response: AiGraphResolvePersonResponse; note: string }
  | { kind: 'search'; response: AiGraphSearchPeopleResponse; note: string }
  | { kind: 'ancestors'; response: AiGraphTraversalResponse; note: string }
  | { kind: 'descendants'; response: AiGraphTraversalResponse; note: string }
  | { kind: 'path'; response: AiGraphRelationshipPathResponse; note: string }
  | { kind: 'focus'; response: AiGraphFocusResponse; note: string }
  | { kind: 'stats'; response: AiGraphTreeStatsResponse; note: string }
  | { kind: 'help'; title: string; detail: string }
  | { kind: 'error'; title: string; detail: string };

const AI_COMMAND_EXAMPLES = [
  'find Carol Smith',
  'search Smith',
  'ancestors of Carol Smith',
  'descendants of Alice Smith',
  'path Alice Smith to Carol Smith',
  'focus Bob Smith',
  'stats',
];

const makeRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `ai-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalize = (value: string) => value.trim().replace(/\s+/g, ' ');

const parseCommand = (value: string): ParsedCommand => {
  const command = normalize(value);
  if (!command) {
    return null;
  }

  if (/^(help|\?)$/i.test(command)) {
    return { kind: 'help' };
  }

  if (/^(stats|summary)$/i.test(command)) {
    return { kind: 'stats' };
  }

  const resolveMatch = command.match(/^(find|resolve|search|focus)\s+(.+)$/i);
  if (resolveMatch) {
    const kind = resolveMatch[1].toLowerCase();
    return { kind: kind === 'search' ? 'search' : (kind as 'resolve' | 'focus'), query: resolveMatch[2] };
  }

  const ancestorMatch = command.match(/^(ancestors|descendants)(?:\s+of)?\s+(.+)$/i);
  if (ancestorMatch) {
    return { kind: ancestorMatch[1].toLowerCase() === 'ancestors' ? 'ancestors' : 'descendants', query: ancestorMatch[2], depth: 3 };
  }

  const pathMatch = command.match(/^(?:relationship\s+)?path\s+(.+?)\s+(?:to|and)\s+(.+)$/i);
  if (pathMatch) {
    return { kind: 'path', fromQuery: pathMatch[1], toQuery: pathMatch[2] };
  }

  return null;
};

const getCommandKindLabel = (kind: CommandKind) => {
  switch (kind) {
    case 'resolve':
      return 'Resolve';
    case 'search':
      return 'Search';
    case 'ancestors':
      return 'Ancestors';
    case 'descendants':
      return 'Descendants';
    case 'path':
      return 'Path';
    case 'focus':
      return 'Focus';
    case 'stats':
      return 'Stats';
    case 'help':
      return 'Help';
  }
};

export const AiCommandPanel: React.FC<AiCommandPanelProps> = ({
  treeId,
  treeName,
  currentUserId,
  onClose,
  onOpenPerson,
  onFocusPerson,
  onSearchResults,
}) => {
  const { t } = useTranslation();
  const [session, setSession] = useState<AiSessionContext | null>(null);
  const [command, setCommand] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AiResult | null>(null);
  const [planDecisions, setPlanDecisions] = useState<Record<string, 'confirmed' | 'dismissed'>>({});
  const [nowUtcMs, setNowUtcMs] = useState(() => Date.now());

  const commandExamples = useMemo(() => AI_COMMAND_EXAMPLES, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNowUtcMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let isActive = true;

    const createSession = async () => {
      setIsInitializing(true);
      setError(null);
      setResult(null);
      setPlanDecisions({});

      try {
        const response = await aiAPI.createSession({
          treeId,
          userId: currentUserId,
        });

        if (!isActive) {
          return;
        }

        setSession(response.data);
      } catch (sessionError) {
        if (!isActive) {
          return;
        }

        setSession(null);
        setError('Unable to create an AI session right now.');
      } finally {
        if (isActive) {
          setIsInitializing(false);
        }
      }
    };

    void createSession();

    return () => {
      isActive = false;
    };
  }, [currentUserId, treeId]);

  const executePlannerPing = async (prompt: string, currentSession: AiSessionContext) => {
    const plannerResponse = await aiAPI.plan({
      requestId: makeRequestId(),
      sessionId: currentSession.sessionId,
      userInput: prompt,
      requestedAtUtc: new Date().toISOString(),
      treeId,
      userId: currentUserId,
      session: currentSession,
    });

    const nextSession = plannerResponse.data.updatedSession ?? currentSession;
    setSession(nextSession);
    return plannerResponse.data;
  };

  const hydrateReadResultPeople = async (readResult: AiReadResult) => {
    const entityIds = new Set<string>();
    const addEntityId = (entityId?: string | null) => {
      const normalized = entityId?.trim();
      if (normalized) {
        entityIds.add(normalized);
      }
    };

    const subject = readResult.subject;
    if (subject && isPersonEntity(subject.entityType)) {
      addEntityId(subject.entityId);
    }

    readResult.items.forEach((entity) => {
      if (isPersonEntity(entity.entityType)) {
        addEntityId(entity.entityId);
      }
    });

    if (entityIds.size === 0) {
      return [];
    }

    const responses = await Promise.all(
      Array.from(entityIds).map(async (entityId) => {
        try {
          const response = await peopleAPI.getPerson(entityId);
          return response.data.data ?? null;
        } catch (error) {
          return null;
        }
      }),
    );

    return responses.filter((person): person is PersonDto => Boolean(person));
  };

  function isPersonEntity(entityType?: string | null) {
    const normalized = entityType?.trim();
    if (!normalized) {
      return false;
    }

    return normalized.toLowerCase().includes('person');
  }

  const applyReadResult = async (plannerResponse: AiPlannerResponse) => {
    const readResult = plannerResponse.readResult;
    if (!readResult) {
      setResult({
        kind: 'planner',
        planner: plannerResponse,
        note: plannerResponse.summary || 'AI planner returned a read response.',
      });
      return;
    }

    const people = await hydrateReadResultPeople(readResult);
    if (readResult.focusEntityId) {
      onFocusPerson(readResult.focusEntityId);
    }
    if (people.length > 0) {
      onSearchResults(readResult.query, people);
    }

    setResult({
      kind: 'read',
      response: readResult,
      people,
      note: readResult.summary || plannerResponse.summary || 'Read-only result loaded.',
    });
  };

  const visiblePlan = getVisiblePlan(result, session, planDecisions);
  const suggestionChips = buildFollowUpSuggestions({
    session,
    visiblePlan,
    subjectNames: getSuggestionSubjects(result, session),
  });

  const selectPerson = async (person: PersonDto) => {
    onFocusPerson(person.id);
    onOpenPerson(person);
    onSearchResults(person.fullName || `${person.firstName} ${person.lastName}`.trim(), [person]);

    try {
      const focusResponse = await aiAPI.getFocus(treeId, person.id);
      setResult({
        kind: 'focus',
        response: focusResponse.data,
        note: `Focused on ${person.fullName || person.firstName}`,
      });
    } catch (focusError) {
      setResult({
        kind: 'error',
        title: 'Focus failed',
        detail: 'The canvas focus request could not be completed.',
      });
    }
  };

  const confirmVisiblePlan = async () => {
    if (!visiblePlan || !session || isBusy) {
      return;
    }

    setIsBusy(true);
    setError(null);

    try {
      const response = await aiAPI.confirmPlan(session.sessionId, {
        planId: visiblePlan.planId,
        commitNote: `Confirm ${visiblePlan.title}`,
      });

      setSession(response.data.updatedSession ?? session);
      setPlanDecisions({});
      setResult({
        kind: 'commit',
        response: response.data,
        note: response.data.summary ?? 'AI plan committed successfully.',
      });
    } catch (commitError) {
      if (axios.isAxiosError(commitError) && commitError.response?.status === 410) {
        const expiredResponse = commitError.response.data as AiPlanCommitResponse | undefined;
        if (expiredResponse?.updatedSession) {
          setSession(expiredResponse.updatedSession);
        }

        setPlanDecisions({});
        setResult({
          kind: 'commit',
          response: expiredResponse ?? {
            sessionId: session.sessionId,
            planId: visiblePlan.planId,
            status: 'Expired',
            actionCategory: 'Mutate',
            warnings: [],
            errors: [],
            summary: 'The pending plan has expired.',
          },
          note: expiredResponse?.summary ?? 'The pending plan has expired and must be re-planned.',
        });
        setError(null);
        return;
      }

      setError('The AI plan could not be committed.');
      setResult({
        kind: 'error',
        title: 'Commit failed',
        detail: 'The AI confirmation request could not be completed.',
      });
    } finally {
      setIsBusy(false);
    }
  };

  const runCommand = async (input = command) => {
    const trimmed = normalize(input);
    if (!trimmed || isBusy) {
      return;
    }

    if (!session) {
      setError('Session is not ready yet.');
      return;
    }

    setCommand(trimmed);
    setIsBusy(true);
    setError(null);

    try {
      let plannerResponse: AiPlannerResponse | null = null;
      try {
        plannerResponse = await executePlannerPing(trimmed, session);
      } catch (plannerError) {
        // Planner availability is optional for FT-248. Keep deterministic graph commands working.
      }
      const parsed = parseCommand(trimmed);

      if (plannerResponse?.status.toLowerCase() === 'providerfailure') {
        // The planner endpoint is still used to keep session history bounded and auditable.
        // The UI routes read-only commands deterministically until the planner provider is configured.
      }

      const plannerReadResponse = plannerResponse?.status.toLowerCase() === 'success' &&
        (plannerResponse.actionCategory?.toLowerCase() === 'read' || plannerResponse.actionCategory?.toLowerCase() === 'navigate')
        ? plannerResponse
        : null;

      if (!parsed && plannerReadResponse) {
        await applyReadResult(plannerReadResponse);
        return;
      }

      if (!parsed) {
        if (plannerResponse?.pendingActionPlan || plannerResponse?.clarification || (plannerResponse?.errors?.length ?? 0) > 0) {
          setResult({
            kind: 'planner',
            planner: plannerResponse!,
            note: plannerResponse?.summary || 'AI planner returned a mutation response.',
          });
          return;
        }

        if (plannerReadResponse) {
          await applyReadResult(plannerReadResponse);
          return;
        }

        onSearchResults(trimmed, []);
        setResult({
          kind: 'help',
          title: 'Unknown command',
          detail: 'Try a command like "find Carol Smith", "ancestors of Bob Smith", or "stats".',
        });
        return;
      }

      switch (parsed.kind) {
        case 'help':
          setResult({
            kind: 'help',
            title: 'Command help',
            detail: `Try: ${commandExamples.join(' | ')}`,
          });
          break;
        case 'stats': {
          const response = await aiAPI.getTreeStats(treeId);
          setResult({
            kind: 'stats',
            response: response.data,
            note: 'Tree stats are loaded from deterministic FTServices data.',
          });
          break;
        }
        case 'resolve':
        case 'search':
        case 'focus': {
          const response = await aiAPI.resolvePerson(treeId, parsed.query);
          if (!response.data.match && response.data.alternatives.length === 0) {
            onSearchResults(parsed.query, []);
            setResult({
              kind: 'help',
              title: 'No match found',
              detail: `No person matched "${parsed.query}".`,
            });
            break;
          }

          setResult({
            kind: 'resolve',
            response: response.data,
            note: response.data.isAmbiguous
              ? 'Multiple people matched this command. Pick one of the alternatives.'
              : 'Deterministic person resolution completed.',
          });
          onSearchResults(
            parsed.query,
            response.data.match ? [response.data.match, ...response.data.alternatives] : response.data.alternatives,
          );

          if (parsed.kind === 'focus' && response.data.match) {
            void selectPerson(response.data.match);
          } else if (parsed.kind === 'search') {
            const searchResponse = await aiAPI.searchPeople(treeId, parsed.query);
            onSearchResults(searchResponse.data.query, searchResponse.data.results);
            setResult({
              kind: 'search',
              response: searchResponse.data,
              note: 'Search results come from deterministic FTServices data.',
            });
          }

          break;
        }
        case 'ancestors':
        case 'descendants': {
          const resolved = await aiAPI.resolvePerson(treeId, parsed.query);
          if (!resolved.data.match) {
            onSearchResults(parsed.query, []);
            setResult({
              kind: 'help',
              title: 'No match found',
              detail: `No person matched "${parsed.query}".`,
            });
            break;
          }

          const response = parsed.kind === 'ancestors'
            ? await aiAPI.getAncestors(treeId, resolved.data.match.id, parsed.depth)
            : await aiAPI.getDescendants(treeId, resolved.data.match.id, parsed.depth);

          setResult({
            kind: parsed.kind,
            response: response.data,
            note: `Loaded ${getCommandKindLabel(parsed.kind)} for ${resolved.data.match.fullName || resolved.data.match.firstName}.`,
          });
          break;
        }
        case 'path': {
          const fromResolved = await aiAPI.resolvePerson(treeId, parsed.fromQuery);
          const toResolved = await aiAPI.resolvePerson(treeId, parsed.toQuery);

          if (!fromResolved.data.match || !toResolved.data.match) {
            setResult({
              kind: 'help',
              title: 'Path lookup failed',
              detail: 'Both people must be resolved before a relationship path can be computed.',
            });
            break;
          }

          const response = await aiAPI.getRelationshipPath(treeId, fromResolved.data.match.id, toResolved.data.match.id);
          setResult({
            kind: 'path',
            response: response.data,
            note: response.data.found
              ? 'Relationship path found.'
              : 'No connecting path was found in the current tree.',
          });
          break;
        }
      }
    } catch (commandError) {
      setError('The AI command could not be completed.');
      setResult({
        kind: 'error',
        title: 'Command failed',
        detail: 'The AI command could not be completed right now.',
      });
    } finally {
      setIsBusy(false);
    }
  };

  const executeFollowUpSuggestion = async (suggestion: FollowUpSuggestion) => {
    switch (suggestion.kind) {
      case 'command':
        await runCommand(suggestion.command);
        break;
      case 'action':
        switch (suggestion.action) {
          case 'confirmPlan':
            await confirmVisiblePlan();
            break;
          case 'resetSession':
            await resetSession();
            break;
          case 'stats':
            await runCommand('stats');
            break;
        }
        break;
    }
  };

  const resetSession = async () => {
    if (!session) {
      return;
    }

    setIsBusy(true);
    setError(null);
    try {
      await aiAPI.resetSession(session.sessionId);
      const response = await aiAPI.createSession({
        treeId,
        userId: currentUserId,
      });
      setSession(response.data);
      setResult(null);
      setPlanDecisions({});
      setCommand('');
    } catch (resetError) {
      setError('Unable to reset the AI session.');
    } finally {
      setIsBusy(false);
    }
  };

  const recentCommands = session?.recentCommands?.slice(0, 5) ?? [];

  return (
    <div className={`flex h-full flex-col ${brandClasses.appShell} ${brandClasses.primaryText}`}>
      <div className={`border-b px-5 py-4 ${brandClasses.subtleBorder}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#8D867E]">AI command surface</p>
            <h2 className="mt-1 text-lg font-semibold text-[#1A1714]">{treeName || 'Current Tree'}</h2>
            <p className="mt-1 text-sm text-[#6B6560]">
              Read-only commands, deterministic graph tools, and bounded session state.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm text-[#6B6560] transition hover:bg-[#EFEAE3] hover:text-[#1A1714]"
          >
            Close
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-2 text-xs text-[#6B6560]">
          <span>{isInitializing ? 'Creating session...' : session ? `Session ${session.sessionId.slice(0, 8)}` : 'No session'}</span>
          <button
            type="button"
            onClick={() => void resetSession()}
            disabled={isBusy || isInitializing || !session}
            className="font-medium text-[#185FA5] transition hover:text-[#0C447C] disabled:cursor-not-allowed disabled:text-[#8D867E]"
          >
            Reset session
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {error && (
          <div className="mb-4 rounded-2xl border border-[#E7B7B7] bg-[#FFF4F4] px-4 py-3 text-sm text-[#8C2F2F]">
            {error}
          </div>
        )}

        <div className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFDF9] p-4 shadow-[0_6px_24px_rgba(95,83,58,0.10)]">
          <label className="block text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">
            Command
          </label>
          <textarea
            value={command}
            onChange={(event) => setCommand(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void runCommand();
              }
            }}
            placeholder={t('panel.commandPlaceholder', { ns: 'ai' })}
            rows={3}
            className="mt-2 w-full rounded-2xl border border-[#DDD7CE] bg-[#FFFFFF] px-3 py-3 text-sm text-[#1A1714] outline-none transition focus:border-[#185FA5] focus:ring-4 focus:ring-[rgba(24,95,165,0.08)]"
          />
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void runCommand()}
              disabled={isBusy || isInitializing}
              className="inline-flex items-center justify-center rounded-xl bg-[#185FA5] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#0C447C] disabled:cursor-not-allowed disabled:bg-[#9E9890]"
            >
              {isBusy ? 'Working...' : 'Run command'}
            </button>
            <button
              type="button"
              onClick={() => setCommand('stats')}
              className="rounded-xl border border-[#DDD7CE] px-3 py-2 text-sm text-[#4A443E] transition hover:bg-[#F3EEE7]"
            >
              Stats
            </button>
            <button
              type="button"
              onClick={() => setCommand('help')}
              className="rounded-xl border border-[#DDD7CE] px-3 py-2 text-sm text-[#4A443E] transition hover:bg-[#F3EEE7]"
            >
              Help
            </button>
          </div>
        </div>

        {commandExamples.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">Examples</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {commandExamples.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => setCommand(example)}
                  className="rounded-full border border-[#DDD7CE] bg-[#FFFDF9] px-3 py-1.5 text-xs text-[#4A443E] transition hover:border-[#185FA5] hover:text-[#185FA5]"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 space-y-3">
          {result ? renderResult(result, onOpenPerson, onFocusPerson, t) : (
            <div className="rounded-2xl border border-dashed border-[#D7D3CC] bg-[#FBFAF7] px-4 py-4 text-sm text-[#6B6560]">
              Run a command to see typed AI results here.
            </div>
          )}
        </div>

        {suggestionChips.length > 0 && (
          <div className="mt-4 rounded-2xl border border-[#E2DDD8] bg-[#FCFBF8] px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">Follow-up context</p>
                <p className="mt-1 text-sm text-[#6B6560]">Suggested next steps from the current bounded session.</p>
              </div>
              <button
                type="button"
                onClick={() => void resetSession()}
                disabled={isBusy || isInitializing || !session}
                className="rounded-xl border border-[#DDD7CE] px-3 py-2 text-xs font-medium text-[#4A443E] transition hover:bg-[#F3EEE7] disabled:cursor-not-allowed disabled:text-[#8D867E]"
              >
                Clear context
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {suggestionChips.map((suggestion) => (
                <button
                  key={`${suggestion.kind}:${suggestion.kind === 'command' ? suggestion.command : suggestion.action}`}
                  type="button"
                  onClick={() => void executeFollowUpSuggestion(suggestion)}
                  disabled={isBusy}
                  title={suggestion.description}
                  className="rounded-full border border-[#DDD7CE] bg-[#FFFDF9] px-3 py-2 text-left text-xs text-[#4A443E] transition hover:border-[#185FA5] hover:text-[#185FA5] disabled:cursor-not-allowed disabled:border-[#E2DDD8] disabled:text-[#9E9890]"
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {visiblePlan && planDecisions[visiblePlan.planId] !== 'dismissed' && (
          <div className="mt-5">
            <AiPlanPreviewCard
              plan={visiblePlan}
              plannerResponse={result?.kind === 'planner' ? result.planner : null}
              nowUtcMs={nowUtcMs}
              isConfirmed={false}
              onConfirm={() => void confirmVisiblePlan()}
              onCancel={() => {
                setPlanDecisions((current) => ({ ...current, [visiblePlan.planId]: 'dismissed' }));
              }}
              />
          </div>
        )}

        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">Recent commands</p>
          <div className="mt-2 space-y-2">
            {recentCommands.length > 0 ? recentCommands.map((item, index) => (
              <div
                key={`${item}-${index}`}
                className="rounded-xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-2 text-sm text-[#4A443E]"
              >
                {item}
              </div>
            )) : (
              <div className="rounded-xl border border-dashed border-[#D7D3CC] bg-[#FBFAF7] px-3 py-2 text-sm text-[#6B6560]">
                No recent commands yet.
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">Recent entities</p>
          <div className="mt-2 space-y-2">
            {session?.recentEntities?.length ? session.recentEntities.slice(0, 5).map((entity) => (
              <div
                key={`${entity.entityType}-${entity.entityId}`}
                className="rounded-xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-2 text-sm text-[#4A443E]"
              >
                <div className="font-medium text-[#1A1714]">{entity.displayName}</div>
                <div className="text-xs text-[#6B6560]">{entity.entityType}</div>
              </div>
            )) : (
              <div className="rounded-xl border border-dashed border-[#D7D3CC] bg-[#FBFAF7] px-3 py-2 text-sm text-[#6B6560]">
                No recent entities.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function renderResult(
  result: AiResult,
  onOpenPerson: (person: PersonDto) => void,
  onFocusPerson: (personId: string) => void,
  t: TFunction,
) {
  switch (result.kind) {
    case 'planner':
      return (
        <ResultCard title={t('panel.planner', { ns: 'ai' })} tone="neutral">
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          {result.planner.clarification && (
            <div className="mt-3 rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8D867E]">Clarification</p>
              <p className="mt-1 text-sm font-medium text-[#1A1714]">{result.planner.clarification.prompt}</p>
              {result.planner.clarification.question && (
                <p className="mt-1 text-sm text-[#6B6560]">{result.planner.clarification.question}</p>
              )}
            </div>
          )}
          {result.planner.errors.length > 0 && (
            <div className="mt-3 space-y-2">
              {result.planner.errors.map((issue) => (
                <div key={issue.code} className="rounded-2xl border border-[#E7B7B7] bg-[#FFF4F4] px-3 py-3 text-sm text-[#8C2F2F]">
                  <p className="font-medium">{issue.message}</p>
                  <p className="mt-1 text-xs text-[#A35A5A]">{issue.code}</p>
                </div>
              ))}
            </div>
          )}
        </ResultCard>
      );
    case 'read': {
      const hydratedIds = new Set(result.people.map((person) => person.id));
      const remainingEntities = result.response.items.filter((entity) => !hydratedIds.has(entity.entityId));

      return (
        <ResultCard title={t('panel.readResult', { ns: 'ai' })} tone="success">
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full border border-[#BFD1A4] bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5B7447]">
              {result.response.mode}
            </span>
            <span className="rounded-full border border-[#E2DDD8] bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8D867E]">
              {result.response.items.length} item{result.response.items.length === 1 ? '' : 's'}
            </span>
            {result.people.length > 0 && (
              <span className="rounded-full border border-[#E2DDD8] bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8D867E]">
                {result.people.length} person{result.people.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {result.response.subject && (
            <div className="mt-3 rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8D867E]">Subject</p>
              <p className="mt-1 text-sm font-medium text-[#1A1714]">{result.response.subject.displayName}</p>
              <p className="mt-1 text-xs text-[#6B6560]">{result.response.subject.entityType}</p>
            </div>
          )}
          {result.people.length > 0 && (
            <div className="mt-3 space-y-2">
              {result.people.map((person) => (
                <PersonResultRow
                  key={person.id}
                  person={person}
                  primaryActionLabel={t('panel.openDetails', { ns: 'ai' })}
                  secondaryActionLabel={t('panel.focusCanvas', { ns: 'ai' })}
                  onPrimaryAction={() => onOpenPerson(person)}
                  onSecondaryAction={() => onFocusPerson(person.id)}
                />
              ))}
            </div>
          )}
          {remainingEntities.length > 0 && (
            <div className="mt-3 rounded-2xl border border-[#E2DDD8] bg-[#FCFBF8] px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8D867E]">Entities</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {remainingEntities.map((entity) => (
                  <div
                    key={`${entity.entityType}-${entity.entityId}`}
                    className="rounded-full border border-[#DDD7CE] bg-white px-3 py-1.5 text-xs text-[#4A443E]"
                    title={entity.entityType}
                  >
                    <span className="font-medium text-[#1A1714]">{entity.displayName}</span>
                    <span className="ml-2 text-[#8D867E]">{entity.entityType}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.people.length === 0 && remainingEntities.length === 0 && (
            <div className="mt-3 rounded-2xl border border-dashed border-[#D7D3CC] bg-[#FBFAF7] px-3 py-3 text-sm text-[#6B6560]">
              No people were returned for this read command.
            </div>
          )}
        </ResultCard>
      );
    }
    case 'commit':
      return (
        <ResultCard title={t('panel.planCommitted', { ns: 'ai' })} tone="success">
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          <p className="mt-2 text-sm text-[#6B6560]">
            {result.response.committedToolName || 'Committed action'}{result.response.committedEntityType ? ` on ${result.response.committedEntityType}` : ''}
          </p>
          {result.response.committedEntityId && (
            <p className="mt-1 text-xs text-[#8D867E]">Entity {result.response.committedEntityId}</p>
          )}
        </ResultCard>
      );
    case 'resolve':
      return (
        <ResultCard title={result.response.isAmbiguous ? 'Multiple matches' : 'Person resolved'} tone={result.response.isAmbiguous ? 'warning' : 'success'}>
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          {result.response.match && (
            <PersonResultRow
              person={result.response.match}
              primaryActionLabel={t('panel.openDetails', { ns: 'ai' })}
              secondaryActionLabel={t('panel.focusCanvas', { ns: 'ai' })}
              onPrimaryAction={() => onOpenPerson(result.response.match!)}
              onSecondaryAction={() => onFocusPerson(result.response.match!.id)}
            />
          )}
          {result.response.alternatives.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8D867E]">Alternatives</p>
              {result.response.alternatives.map((person) => (
                <PersonResultRow
                  key={person.id}
                  person={person}
                  primaryActionLabel={t('panel.openDetails', { ns: 'ai' })}
                  secondaryActionLabel={t('panel.focusCanvas', { ns: 'ai' })}
                  onPrimaryAction={() => onOpenPerson(person)}
                  onSecondaryAction={() => onFocusPerson(person.id)}
                />
              ))}
            </div>
          )}
        </ResultCard>
      );
    case 'search':
      return (
        <ResultCard title={t('panel.searchResults', { ns: 'ai' })} tone="neutral">
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          <div className="mt-3 space-y-2">
            {result.response.results.length > 0 ? result.response.results.map((person) => (
              <PersonResultRow
                key={person.id}
                person={person}
                primaryActionLabel={t('panel.openDetails', { ns: 'ai' })}
                secondaryActionLabel={t('panel.focusCanvas', { ns: 'ai' })}
                onPrimaryAction={() => onOpenPerson(person)}
                onSecondaryAction={() => onFocusPerson(person.id)}
              />
            )) : (
              <p className="text-sm text-[#6B6560]">No matches.</p>
            )}
          </div>
        </ResultCard>
      );
    case 'ancestors':
    case 'descendants':
      return (
        <ResultCard title={getCommandKindLabel(result.kind)} tone="neutral">
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          <p className="mt-2 text-sm text-[#6B6560]">
            {result.response.relatedPeople.length > 0
              ? result.response.relatedPeople.map((person) => person.fullName || person.firstName).join(' -> ')
              : 'No related people were found at the requested depth.'}
          </p>
        </ResultCard>
      );
    case 'path':
      return (
        <ResultCard title={t('panel.relationshipPath', { ns: 'ai' })} tone={result.response.found ? 'success' : 'warning'}>
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          <p className="mt-2 text-sm text-[#6B6560]">
            {result.response.path.map((person) => person.fullName || person.firstName).join(' -> ') || 'No path.'}
          </p>
        </ResultCard>
      );
    case 'focus':
      return (
        <ResultCard title={t('panel.focusCanvas', { ns: 'ai' })} tone="success">
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          {result.response.person && (
            <PersonResultRow
              person={result.response.person}
              primaryActionLabel={t('panel.openDetails', { ns: 'ai' })}
              secondaryActionLabel={t('panel.focusCanvas', { ns: 'ai' })}
              onPrimaryAction={() => onOpenPerson(result.response.person!)}
              onSecondaryAction={() => onFocusPerson(result.response.person!.id)}
            />
          )}
          <p className="mt-3 text-sm text-[#6B6560]">
            Viewport: zoom {result.response.viewport.zoomLevel.toFixed(2)}, pan {Math.round(result.response.viewport.panX)}, {Math.round(result.response.viewport.panY)}
          </p>
        </ResultCard>
      );
    case 'stats':
      return (
        <ResultCard title={t('panel.treeStats', { ns: 'ai' })} tone="neutral">
          <p className="text-sm text-[#4A443E]">{result.note}</p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <StatChip label={t('panel.people', { ns: 'ai' })} value={result.response.peopleCount} />
            <StatChip label={t('panel.relationships', { ns: 'ai' })} value={result.response.relationshipCount} />
            <StatChip label={t('panel.edges', { ns: 'ai' })} value={result.response.directedEdgeCount} />
            <StatChip label={t('panel.roots', { ns: 'ai' })} value={result.response.rootCandidateCount} />
          </div>
        </ResultCard>
      );
    case 'help':
      return (
        <ResultCard title={result.title} tone="neutral">
          <p className="text-sm text-[#4A443E]">{result.detail}</p>
        </ResultCard>
      );
    case 'error':
      return (
        <ResultCard title={result.title} tone="danger">
          <p className="text-sm text-[#4A443E]">{result.detail}</p>
        </ResultCard>
      );
  }
}

function getSuggestionSubjects(result: AiResult | null, session: AiSessionContext | null): string[] {
  const subjects: string[] = [];
  const add = (value?: string | null) => {
    const normalized = value?.trim();
    if (normalized && !subjects.includes(normalized)) {
      subjects.push(normalized);
    }
  };

  const addPerson = (person?: PersonDto | null) => {
    if (!person) {
      return;
    }

    add(person.fullName || `${person.firstName} ${person.lastName}`.trim());
  };

  if (result) {
    switch (result.kind) {
      case 'resolve':
        addPerson(result.response.match);
        result.response.alternatives.slice(0, 1).forEach(addPerson);
        break;
      case 'read':
        add(result.response.subject?.displayName);
        result.people.slice(0, 2).forEach(addPerson);
        result.response.items.slice(0, 2).forEach((entity) => add(entity.displayName));
        break;
      case 'search':
        result.response.results.slice(0, 2).forEach(addPerson);
        break;
      case 'ancestors':
      case 'descendants':
        addPerson(result.response.person);
        result.response.relatedPeople.slice(0, 1).forEach(addPerson);
        break;
      case 'path':
        result.response.path.slice(0, 2).forEach(addPerson);
        break;
      case 'focus':
        addPerson(result.response.person);
        break;
      case 'commit':
        add(session?.recentEntities[0]?.displayName);
        break;
      case 'planner':
      case 'stats':
      case 'help':
      case 'error':
        break;
    }
  }

  session?.recentEntities.slice(0, 3).forEach((entity) => add(entity.displayName));

  return subjects.slice(0, 3);
}

function getVisiblePlan(
  result: AiResult | null,
  session: AiSessionContext | null,
  planDecisions: Record<string, 'confirmed' | 'dismissed'>,
): AiPendingActionPlan | null {
  const candidate = result?.kind === 'planner' && result.planner.pendingActionPlan
    ? result.planner.pendingActionPlan
    : session?.pendingActionPlan ?? null;

  if (!candidate) {
    return null;
  }

  if (planDecisions[candidate.planId] === 'dismissed') {
    return null;
  }

  return candidate;
}

const ResultCard: React.FC<{ title: string; tone: 'neutral' | 'success' | 'warning' | 'danger'; children: React.ReactNode }> = ({ title, tone, children }) => {
  const toneClasses = {
    neutral: 'border-[#E2DDD8] bg-[#FFFDF9]',
    success: 'border-[#BFD1A4] bg-[#F7FBF0]',
    warning: 'border-[#F0D2A8] bg-[#FFFAF2]',
    danger: 'border-[#E7B7B7] bg-[#FFF4F4]',
  }[tone];

  return (
    <div className={`rounded-[24px] border p-4 ${toneClasses}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">{title}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
};

const PersonResultRow: React.FC<{
  person: PersonDto;
  primaryActionLabel: string;
  secondaryActionLabel: string;
  onPrimaryAction: () => void;
  onSecondaryAction: () => void;
}> = ({ person, primaryActionLabel, secondaryActionLabel, onPrimaryAction, onSecondaryAction }) => {
  return (
    <div className="rounded-2xl border border-[#E2DDD8] bg-white px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#1A1714]">{person.fullName || `${person.firstName} ${person.lastName}`.trim()}</p>
          <p className="text-xs text-[#6B6560]">{person.gender || 'Unspecified'}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={onPrimaryAction}
            className="rounded-lg border border-[#DDD7CE] px-2.5 py-1 text-xs text-[#4A443E] transition hover:bg-[#F3EEE7]"
          >
            {primaryActionLabel}
          </button>
          <button
            type="button"
            onClick={onSecondaryAction}
            className="rounded-lg bg-[#185FA5] px-2.5 py-1 text-xs text-white transition hover:bg-[#0C447C]"
          >
            {secondaryActionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatChip: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-2xl border border-[#E2DDD8] bg-white px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8D867E]">{label}</p>
    <p className="mt-1 text-lg font-semibold text-[#1A1714]">{value}</p>
  </div>
);
